import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Upload, 
  X, 
  Eye,
  FileUp,
  Download,
  FileSpreadsheet,
  Loader2,
  ImagePlus,
  GripVertical,
  XCircle,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultEngine = {
  title: '',
  brand: '',
  model: '',
  horsepower: 0,
  engine_type: 'Inboard',
  fuel_type: 'Diesel',
  cylinders: 4,
  displacement: 0,
  weight: 0,
  rpm_range: '',
  price: 0,
  description: '',
  features: [],
  images: [],
  is_featured: false,
  is_bestseller: false,
  is_new: false,
  status: 'active',
};

export default function AdminCatalogPage() {
  const { token } = useAuth();
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [formData, setFormData] = useState(defaultEngine);
  const [featuresInput, setFeaturesInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const csvInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchEngines();
  }, [page, search]);

  const fetchEngines = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 15);
      
      const response = await axios.get(`${API_URL}/api/engines?${params}`);
      setEngines(response.data.engines);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch engines');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (engine) => {
    const newVal = !engine.is_featured;
    // optimistic update
    setEngines((prev) => prev.map((e) => (e.id === engine.id ? { ...e, is_featured: newVal } : e)));
    try {
      await axios.put(
        `${API_URL}/api/engines/${engine.id}/feature?is_featured=${newVal}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newVal ? 'Marked as featured' : 'Removed from featured');
    } catch (error) {
      // revert on failure
      setEngines((prev) => prev.map((e) => (e.id === engine.id ? { ...e, is_featured: !newVal } : e)));
      toast.error('Failed to update featured status');
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'engines_inventory.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Inventory exported successfully');
    } catch (error) {
      toast.error('Failed to export inventory');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'engines_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCreate = () => {
    setSelectedEngine(null);
    setFormData(defaultEngine);
    setFeaturesInput('');
    setIsModalOpen(true);
  };

  const handleEdit = (engine) => {
    setSelectedEngine(engine);
    setFormData(engine);
    setFeaturesInput(engine.features?.join('\n') || '');
    setIsModalOpen(true);
  };

  const handleDelete = (engine) => {
    setSelectedEngine(engine);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/engines/${selectedEngine.id}`);
      toast.success('Engine deleted successfully');
      fetchEngines();
    } catch (error) {
      toast.error('Failed to delete engine');
    } finally {
      setIsDeleteOpen(false);
      setSelectedEngine(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      features: featuresInput.split('\n').filter(f => f.trim()),
    };

    try {
      if (selectedEngine) {
        await axios.put(`${API_URL}/api/engines/${selectedEngine.id}`, data);
        toast.success('Engine updated successfully');
      } else {
        await axios.post(`${API_URL}/api/engines`, data);
        toast.success('Engine created successfully');
      }
      setIsModalOpen(false);
      fetchEngines();
    } catch (error) {
      toast.error('Failed to save engine');
    } finally {
      setSaving(false);
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/inventory/import-csv`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      
      const { imported, updated, errors, total_errors } = response.data;
      
      if (imported > 0 || updated > 0) {
        toast.success(`Imported ${imported} new engines, updated ${updated} existing`);
      }
      if (total_errors > 0) {
        toast.warning(`${total_errors} rows had errors`);
        console.log('Import errors:', errors);
      }
      fetchEngines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
    
    e.target.value = '';
  };

  // Handle multiple image upload
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    const formDataObj = new FormData();
    
    Array.from(files).forEach((file) => {
      formDataObj.append('files', file);
    });
    
    try {
      const response = await axios.post(`${API_URL}/api/upload/multiple`, formDataObj, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      
      // Add new image URLs to formData
      const newUrls = response.data.map(item => item.url);
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      
      toast.success(`${response.data.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
      console.error('Upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle file input change
  const handleImageInputChange = (e) => {
    handleImageUpload(e.target.files);
    e.target.value = '';
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleImageUpload(e.dataTransfer.files);
  };

  // Remove image from list
  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Move image up in order
  const handleMoveImage = (fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= formData.images.length) return;
    
    setFormData(prev => {
      const newImages = [...prev.images];
      [newImages[fromIndex], newImages[toIndex]] = [newImages[toIndex], newImages[fromIndex]];
      return { ...prev, images: newImages };
    });
  };

  // Get full image URL for display
  const getDisplayImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/uploads/')) return `${API_URL}${url}`;
    if (url.startsWith('/uploads/')) return `${API_URL}/api${url}`;
    return `${API_URL}/api/uploads/${url}`;
  };

  return (
    <div data-testid="admin-inventory-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Engine Inventory</h1>
          <p className="text-slate-600">Manage your marine engine inventory</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="text-slate-600"
            data-testid="download-template-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={exporting}
            className="text-green-600 border-green-300 hover:bg-green-50"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
          <input
            type="file"
            accept=".csv"
            ref={csvInputRef}
            onChange={handleCSVImport}
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={() => csvInputRef.current?.click()}
            disabled={importing}
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
            data-testid="import-csv-btn"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="w-4 h-4 mr-2" />
            )}
            Import CSV
          </Button>
          <Button onClick={handleCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="create-engine-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Engine
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search engines..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
            data-testid="inventory-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Engine</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead className="hidden md:table-cell">HP</TableHead>
              <TableHead className="hidden lg:table-cell">Type</TableHead>
              <TableHead className="hidden lg:table-cell">Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <div className="h-8 bg-slate-100 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : engines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  No engines found
                </TableCell>
              </TableRow>
            ) : (
              engines.map((engine) => (
                <TableRow key={engine.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img
                        src={getDisplayImageUrl(engine.images?.[0]) || 'https://images.pexels.com/photos/35591861/pexels-photo-35591861.jpeg'}
                        alt=""
                        className="w-12 h-8 object-cover rounded-sm"
                        onError={(e) => {
                          e.target.src = 'https://images.pexels.com/photos/35591861/pexels-photo-35591861.jpeg';
                        }}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate max-w-[200px]">{engine.title}</p>
                        <p className="text-xs text-slate-500">{engine.model}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{engine.brand}</TableCell>
                  <TableCell className="hidden md:table-cell font-mono">{engine.horsepower}</TableCell>
                  <TableCell className="hidden lg:table-cell">{engine.engine_type}</TableCell>
                  <TableCell className="hidden lg:table-cell font-mono">
                    {engine.price ? `$${engine.price.toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {engine.is_featured && <Badge className="text-xs bg-orange-100 text-orange-600">Featured</Badge>}
                      {engine.is_bestseller && <Badge className="text-xs bg-emerald-100 text-emerald-600">Bestseller</Badge>}
                      {engine.is_new && <Badge className="text-xs bg-sky-100 text-sky-600">New</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleFeatured(engine)}
                        title={engine.is_featured ? 'Unfeature' : 'Feature'}
                        className={engine.is_featured ? 'text-orange-500 hover:text-orange-600' : 'text-slate-400 hover:text-orange-500'}
                        data-testid={`feature-toggle-${engine.id}`}
                      >
                        <Star className={`w-4 h-4 ${engine.is_featured ? 'fill-orange-500' : ''}`} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(engine)} data-testid={`edit-${engine.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(engine)} data-testid={`delete-${engine.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-slate-200 p-4 flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEngine ? 'Edit Engine' : 'Create New Engine'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="brand">Brand *</Label>
                <Input
                  id="brand"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="horsepower">Horsepower *</Label>
                <Input
                  id="horsepower"
                  type="number"
                  value={formData.horsepower}
                  onChange={(e) => setFormData({ ...formData, horsepower: parseInt(e.target.value) || 0 })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="engine_type">Engine Type</Label>
                <Select value={formData.engine_type} onValueChange={(v) => setFormData({ ...formData, engine_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inboard">Inboard</SelectItem>
                    <SelectItem value="Outboard">Outboard</SelectItem>
                    <SelectItem value="Sterndrive">Sterndrive</SelectItem>
                    <SelectItem value="Pod Drive">Pod Drive</SelectItem>
                    <SelectItem value="Jet Drive">Jet Drive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fuel_type">Fuel Type</Label>
                <Select value={formData.fuel_type} onValueChange={(v) => setFormData({ ...formData, fuel_type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="Gasoline">Gasoline</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cylinders">Cylinders</Label>
                <Input
                  id="cylinders"
                  type="number"
                  value={formData.cylinders}
                  onChange={(e) => setFormData({ ...formData, cylinders: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="displacement">Displacement (L)</Label>
                <Input
                  id="displacement"
                  type="number"
                  step="0.1"
                  value={formData.displacement}
                  onChange={(e) => setFormData({ ...formData, displacement: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="rpm_range">RPM Range</Label>
                <Input
                  id="rpm_range"
                  value={formData.rpm_range}
                  onChange={(e) => setFormData({ ...formData, rpm_range: e.target.value })}
                  placeholder="e.g., 2000-4500"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Multi-Image Upload Section */}
            <div>
              <Label className="mb-2 block">Engine Images</Label>
              
              {/* Current Images */}
              {formData.images && formData.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                  {formData.images.map((img, index) => (
                    <div 
                      key={index} 
                      className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50"
                    >
                      <img
                        src={getDisplayImageUrl(img)}
                        alt={`Engine image ${index + 1}`}
                        className="w-full h-24 object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg?auto=compress&cs=tinysrgb&w=200';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        {index > 0 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-white hover:bg-white/20"
                            onClick={() => handleMoveImage(index, 'up')}
                            title="Move left"
                          >
                            <GripVertical className="h-4 w-4 rotate-90" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-400 hover:bg-red-500/20 hover:text-white"
                          onClick={() => handleRemoveImage(index)}
                          title="Remove image"
                        >
                          <XCircle className="h-5 w-5" />
                        </Button>
                      </div>
                      {index === 0 && (
                        <div className="absolute top-1 left-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">
                          Main
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  dragOver 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-slate-300 hover:border-orange-400 hover:bg-slate-50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => imageInputRef.current?.click()}
                data-testid="image-upload-zone"
              >
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageInputChange}
                  className="hidden"
                  data-testid="image-file-input"
                />
                {uploadingImages ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-2" />
                    <p className="text-sm text-slate-600">Uploading images...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <ImagePlus className="h-10 w-10 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-700">
                      Drop images here or click to upload
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Supports JPG, PNG, GIF, WebP (Multiple files allowed)
                    </p>
                  </div>
                )}
              </div>

              {/* Add URL manually */}
              <div className="mt-3 flex gap-2">
                <Input
                  placeholder="Or paste image URL..."
                  id="manual-image-url"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const url = e.target.value.trim();
                      if (url) {
                        setFormData(prev => ({
                          ...prev,
                          images: [...(prev.images || []), url]
                        }));
                        e.target.value = '';
                      }
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.getElementById('manual-image-url');
                    const url = input.value.trim();
                    if (url) {
                      setFormData(prev => ({
                        ...prev,
                        images: [...(prev.images || []), url]
                      }));
                      input.value = '';
                    }
                  }}
                >
                  Add URL
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="features">Features (one per line)</Label>
              <Textarea
                id="features"
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
                rows={4}
                placeholder="Electronic fuel injection&#10;Corrosion-resistant materials&#10;Low emissions"
                className="mt-1"
              />
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })}
                />
                <Label htmlFor="is_featured" className="cursor-pointer">Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_bestseller"
                  checked={formData.is_bestseller}
                  onCheckedChange={(c) => setFormData({ ...formData, is_bestseller: c })}
                />
                <Label htmlFor="is_bestseller" className="cursor-pointer">Bestseller</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_new"
                  checked={formData.is_new}
                  onCheckedChange={(c) => setFormData({ ...formData, is_new: c })}
                />
                <Label htmlFor="is_new" className="cursor-pointer">New Arrival</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={saving}>
                {saving ? 'Saving...' : selectedEngine ? 'Update Engine' : 'Create Engine'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Engine</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedEngine?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
